import * as React from "react";
import {connect} from 'react-redux';
import * as selectors from "../selectors";
import {DisplayFeature, ValueType, FeatureAttributeChange} from "../domain";
import classNames = require("classnames");
import datafangstHelpers from "../../../../utilities";
import {onAttributeEdit} from "../../../actions/actions";
import {AttributeValueValidator, ValidationResult} from "../domain/AttributeValueValidator";
import {CloseEditor, closeEditor} from "../reducers/actions";
import {AttributeType, EnumerationType} from "../../../../integration/datacatalog/datacatalog-services";
import FeaturesState from "../domain/FeaturesState";
import {AttributeModel} from "../../../../integration/representation/viewmodel";
import {Spinner} from "../../../shared-components/status/Spinner";

interface CellState {
    isValid: boolean;
}

interface StateProps {
    showEditor?: boolean;
}

interface TagProps {
    feature: DisplayFeature;
    featureAttributeType: AttributeType;
    hasFocus: boolean;
    loading: boolean;
}

interface DispatchProps {
    updateAttribute?: Function;
}

type CellProps = StateProps & TagProps & DispatchProps;

class AttributeCell extends React.Component<CellProps, CellState> {

    constructor(props: CellProps) {
        super(props);
        this.state = {isValid: true}
    }

    render() {

        let {featureAttributeType, feature, updateAttribute, loading} = this.props;
        const attribute: AttributeModel = this.props.feature.attributes.find(attr => attr.typeId === this.props.featureAttributeType.id);

        let cellValueClasses = classNames({
            'cell-component': true,
            'cell-focus': this.props.hasFocus,
            'invalid': !this.state.isValid,
            'edited': attribute && (attribute.operation === "WRITE" || attribute.operation === "DELETE")
        });

        let attributeValue = !!attribute ? attribute.value : null;
        if (attributeValue && featureAttributeType.unit) {
            attributeValue = attributeValue + " " + featureAttributeType.unit;
        }
        const editor = this.props.hasFocus && this.props.showEditor && !loading ?
            <Editor
                attribute={attribute}
                featureAttributeType={featureAttributeType}
                updateAttribute={updateAttribute}
                feature={feature}
                setValidationState={this.setValidationState.bind(this)}
            /> : null;

        if(loading){
            return <div className={"cssloader small"}/> ;
        }

        return (
            <div className={cellValueClasses}>
                {editor || attributeValue}
            </div>
        );
    }

    setValidationState(validationState: boolean) {
        this.setState({isValid: validationState});
    }
}

const mapStateToProps = (state: FeaturesState): StateProps => {
    return {
        showEditor: selectors.showEditor(state)
    }
};

const mapDispatchToProps = ({
    updateAttribute: onAttributeEdit
});

export default connect(mapStateToProps, mapDispatchToProps)(AttributeCell);


interface EditorState {

}

interface EditorProps {
    featureAttributeType: AttributeType;
    attribute: AttributeModel;
    updateAttribute: Function;
    feature: DisplayFeature;
    setValidationState: Function;
}

const compareEnums = (a:EnumerationType, b:EnumerationType):number => {
    if (a.sortNum > b.sortNum){
        return 1;
    }
    else if (a.sortNum < b.sortNum){
        return -1;
    }
    else {
        return 0;
    }
}

class Editor extends React.Component<EditorProps, EditorState> {
    render() {

        let {featureAttributeType, attribute, feature, updateAttribute, setValidationState} = this.props;

        let valueType = featureAttributeType.valueType + '';
        switch (valueType) {
            case ValueType[ValueType.NUMBER]:
            case ValueType[ValueType.STRING]:
                return (
                    <TextEditor
                        attribute={attribute}
                        featureAttributeType={featureAttributeType}
                        updateAttribute={updateAttribute}
                        feature={feature}
                        setValidationState={setValidationState}
                    />
                );

            case ValueType[ValueType.ENUM]:
                let enums: Array<EnumerationType> = datafangstHelpers.values(featureAttributeType.enumerations);
                enums.sort(compareEnums);
                let options: Array<string> = enums
                    .map((enumeration) => enumeration.value);
                return (
                    <DropDownEditor
                        options={options}
                        attribute={attribute}
                        feature={feature}
                        updateAttribute={updateAttribute}
                        featureAttributeType={featureAttributeType}
                    />
                );
            default:
                return null;
        }
    }
}

interface TextEditorProps {
    attribute: AttributeModel;
    featureAttributeType: AttributeType;
    updateAttribute: Function;
    feature: DisplayFeature;
    setValidationState: Function;
    doCloseEditor: CloseEditor;
}

interface TextEditorState {
    localValue: string;
}


const mapTEStateToProps = () => {
    return {}
};

const mapTEDispatchToProps = ({
    doCloseEditor: closeEditor
});

class TextEditorComponent extends React.Component<TextEditorProps, TextEditorState> {

    valueInput: HTMLInputElement;
    saveOnClose: boolean;

    constructor(props: TextEditorProps) {
        super(props);
        this.state = {localValue: props.attribute && props.attribute.value ? props.attribute.value : ""};
        this.valueInput = null;
        this.saveOnClose = true;
    }

    render() {
        return (
            <span>
                <input
                    ref={(input) => this.valueInput = input}
                    type="text" value={this.state.localValue}
                    onChange={this.handleChange.bind(this)}
                    onKeyUp={this.handleKeyUp.bind(this)}
                />
            </span>
        )
    }

    handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        let value = e.target.value;
        this.setState({localValue: value});
        let validationResult: ValidationResult = AttributeValueValidator.validate(this.props.featureAttributeType, value);
        if (validationResult.isValid) {
            this.props.setValidationState(true)
        } else {
            this.props.setValidationState(false);
        }
    };

    handleKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
        // close the editor, which triggers unmount and save.
        if (e.key === "Enter") {
            this.props.doCloseEditor();
        }
        if (e.key === "Escape") {
            this.saveOnClose = false;
            this.props.doCloseEditor();
        }
    }

    validateAndSave() {
        let {feature, attribute, featureAttributeType, updateAttribute} = this.props;
        let validationResult: ValidationResult = AttributeValueValidator.validate(this.props.featureAttributeType, this.state.localValue);
        if (validationResult.isValid) {
            let oldValue = attribute ? attribute.value : null;
            let newValue = this.state.localValue === "" ? null : this.state.localValue;
            let edit = new FeatureAttributeChange(feature.id, oldValue, newValue, featureAttributeType.id, feature.typeId);
            updateAttribute(edit);
        }

    }

    componentDidMount(): void {
        this.valueInput.focus();
        this.valueInput.setSelectionRange(0, this.valueInput.value.length);
    }

    componentWillUnmount(): void {
        if (this.saveOnClose) {
            this.validateAndSave();
        }
    };
}

const TextEditor = connect(mapTEStateToProps, mapTEDispatchToProps)(TextEditorComponent);


interface DropDownEditorProps {
    options: Array<string>;
    attribute: AttributeModel;
    featureAttributeType: AttributeType;
    updateAttribute: Function;
    feature: DisplayFeature;
    doCloseEditor: CloseEditor
}

interface DropDownEditorState {
    newValue: any;

}

const mapDDEStateToProps = () => {
    return {}
};

const mapDDEDispatchToProps = ({
    doCloseEditor: closeEditor
});


class DropDownEditorComponent extends React.Component<DropDownEditorProps, DropDownEditorState> {

    private inputElement: HTMLSelectElement;
    private saveOnClose: boolean;

    constructor(props: DropDownEditorProps) {
        super(props);
        this.saveOnClose = true;
        this.state = {
            newValue: null
        }
    }

    render() {

        let {options, attribute} = this.props;
        let currentValue = attribute ? attribute.value : "";

        return (
            <span>
                <select value={this.state.newValue || currentValue}
                        ref={(elm) => this.inputElement = elm}
                        onKeyDown={(e) => this.handleKeyDown(e)}
                        onChange={(e) => this.handleValueChange(e)}
                >
                    <option value="">(Ingen verdi)</option>
                    {options.map(option => <option key={option} value={option}>{option}</option>)}
                </select>
            </span>
        )
    }

    componentDidMount(): void {
        this.inputElement.focus();
    }

    componentWillUnmount(): void {
        let {feature, attribute, featureAttributeType, updateAttribute} = this.props;
        if (this.state.newValue !== null) {
            let oldValue = attribute ? attribute.value : null;
            let newValue = this.state.newValue === "" ? null : this.state.newValue;
            let edit = new FeatureAttributeChange(feature.id, oldValue, newValue, featureAttributeType.id, feature.typeId);
            updateAttribute(edit);
        }
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLSelectElement>) {
        switch (event.key) {
            case "Escape":
                event.preventDefault();
                event.stopPropagation();
                this.props.doCloseEditor();
                break;
        }
    }

    handleValueChange(event: React.ChangeEvent<HTMLSelectElement>) {
        this.setState({
            newValue: event.target.value
        }, () => {
            this.props.doCloseEditor();
        });
    }
}

const DropDownEditor = connect(mapDDEStateToProps, mapDDEDispatchToProps)(DropDownEditorComponent);

